import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBox,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronDown,
  FaFilter,
  FaHeart,
  FaInfoCircle,
  FaMinus,
  FaPlus,
  FaRegTrashAlt,
  FaShoppingCart,
  FaStar,
  FaTruck,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import MarketplaceRelatedProductsSection from "../marketplace/MarketplaceRelatedProductsSection";
import {
  addMarketplaceSavedListToCart,
  addMarketplaceWishlistItem,
  moveMarketplaceSavedListItems,
  removeMarketplaceSavedListItem,
  updateMarketplaceSavedListItem,
} from "../../services/marketplaceCart.service";
import { formatCartInr, normalizeMarketplaceCart } from "../../utils/marketplaceCart.util";
import "../../assets/styles/my_saved_list_detail.scss";

function ProductThumb({ name, imageUrl }) {
  if (imageUrl) {
    return (
      <div className="sld-item__thumb">
        <img src={resolvePublicFileUrl(imageUrl)} alt={name} />
      </div>
    );
  }
  return (
    <div className="sld-item__thumb" aria-hidden>
      <FaBox />
    </div>
  );
}

function StarRating({ value = 4.8, className = "sld-item__rating" }) {
  return (
    <div className={className} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <FaStar key={star} color={star <= Math.round(value) ? "#f97316" : "#e2e8f0"} />
      ))}
      <span>{value}</span>
    </div>
  );
}

function sortSuppliers(suppliers, sortBy) {
  const next = suppliers.map((supplier) => ({
    ...supplier,
    items: [...(supplier.items || [])],
  }));
  next.forEach((supplier) => {
    supplier.items.sort((a, b) => {
      if (sortBy === "name") return String(a.name).localeCompare(String(b.name));
      if (sortBy === "price") return (Number(a.unitPrice) || 0) - (Number(b.unitPrice) || 0);
      return Number(b.id) - Number(a.id);
    });
  });
  return next;
}

export default function MySavedListDetailSection({
  listId,
  detail,
  setDetail,
  allLists,
  saving,
  setSaving,
  onRename,
  onDelete,
}) {
  const moveMenuRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sortBy, setSortBy] = useState("recent");
  const [stockFilter, setStockFilter] = useState("all");
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);

  const suppliers = detail.suppliers || [];
  const allItems = useMemo(() => suppliers.flatMap((s) => s.items || []), [suppliers]);

  const visibleSuppliers = useMemo(() => {
    let next = sortSuppliers(suppliers, sortBy);
    if (stockFilter === "in-stock") {
      next = next
        .map((supplier) => ({
          ...supplier,
          items: (supplier.items || []).filter((item) => item.inStock),
        }))
        .filter((supplier) => supplier.items.length > 0);
    }
    return next;
  }, [suppliers, sortBy, stockFilter]);

  const visibleItems = useMemo(
    () => visibleSuppliers.flatMap((supplier) => supplier.items || []),
    [visibleSuppliers]
  );

  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((item) => selectedIds.has(item.id));

  const estimatedTotal = allItems.reduce(
    (sum, item) => sum + (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
    0
  );
  const estDelivery = suppliers[0]?.estDelivery || "5 - 10 Days";
  const selectedCount = selectedIds.size || allItems.length;
  const otherLists = (allLists || []).filter((list) => String(list.id) !== String(listId));

  useEffect(() => {
    setSelectedIds(new Set());
  }, [listId]);

  useEffect(() => {
    if (!moveMenuOpen) return undefined;
    const onDocClick = (event) => {
      if (moveMenuRef.current?.contains(event.target)) return;
      setMoveMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [moveMenuOpen]);

  const applyDetail = (data) => {
    const normalized = normalizeMarketplaceCart(data);
    setDetail({
      list: data?.list || normalized?.list || detail.list,
      totalItems: normalized?.totalItems || data?.totalItems || 0,
      suppliers: normalized?.suppliers || [],
    });
  };

  const runMutation = async (fn) => {
    if (saving) return null;
    setSaving(true);
    try {
      return await fn();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Action failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const getSelectedProductIds = () => {
    const ids = selectedIds.size
      ? allItems.filter((item) => selectedIds.has(item.id)).map((item) => item.productId)
      : allItems.map((item) => item.productId);
    return ids;
  };

  const toggleItem = (itemId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleItems.map((item) => item.id)));
  };

  const handleQuantityChange = async (item, delta) => {
    const moq = Math.max(1, Number(item.moq) || 1);
    const nextQty = Math.max(moq, (Number(item.quantity) || moq) + delta);
    const data = await runMutation(() => updateMarketplaceSavedListItem(listId, item.productId, nextQty));
    if (data) applyDetail(data);
  };

  const handleAddToCart = async (productIds) => {
    const ids = productIds || getSelectedProductIds();
    const data = await runMutation(() => addMarketplaceSavedListToCart(listId, ids));
    if (data) {
      const count = Number(data.addedCount) || ids.length;
      toast.success(`${count} item${count === 1 ? "" : "s"} added to cart.`);
    }
  };

  const handleMoveToWishlist = async (item) => {
    const data = await runMutation(() => addMarketplaceWishlistItem(item.productId));
    if (data) toast.success(`${item.name} moved to wishlist.`);
  };

  const handleRemove = async (item) => {
    const data = await runMutation(() => removeMarketplaceSavedListItem(listId, item.productId));
    if (data) {
      applyDetail(data);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
      toast.info("Removed from list.");
    }
  };

  const handleMoveToList = async (targetListId) => {
    const productIds = getSelectedProductIds();
    if (!productIds.length) {
      toast.warning("Select at least one item.");
      return;
    }
    const data = await runMutation(() => moveMarketplaceSavedListItems(listId, targetListId, productIds));
    if (data) {
      applyDetail(data);
      setSelectedIds(new Set());
      setMoveMenuOpen(false);
      toast.success("Items moved to another list.");
    }
  };

  if (!allItems.length) {
    return (
      <div className="mysavedlists-empty">
        <h2>This list is empty</h2>
        <p>Items you save from your cart will appear here.</p>
        <Link to="/my-cart" className="mysavedlists-btn mysavedlists-btn--primary">
          Go to Cart
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="sld-summary" aria-label="List summary">
        <div className="sld-summary__card">
          <p className="sld-summary__label">
            <span className="sld-summary__icon sld-summary__icon--orange"><FaBox /></span>
            Total Products
          </p>
          <p className="sld-summary__value">{allItems.length} Item{allItems.length === 1 ? "" : "s"}</p>
        </div>
        <div className="sld-summary__card">
          <p className="sld-summary__label">
            <span className="sld-summary__icon sld-summary__icon--blue"><FaUsers /></span>
            Suppliers
          </p>
          <p className="sld-summary__value">{suppliers.length} Supplier{suppliers.length === 1 ? "" : "s"}</p>
        </div>
        <div className="sld-summary__card">
          <p className="sld-summary__label">
            <span className="sld-summary__icon sld-summary__icon--green"><FaWallet /></span>
            Estimated Value
          </p>
          <p className="sld-summary__value">{formatCartInr(estimatedTotal)}</p>
        </div>
        <div className="sld-summary__card">
          <p className="sld-summary__label">
            <span className="sld-summary__icon sld-summary__icon--purple"><FaCalendarAlt /></span>
            Last Updated
          </p>
          <p className="sld-summary__value">
            {detail.list?.updatedAt
              ? new Date(detail.list.updatedAt).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </p>
        </div>
        <div className="sld-summary__card">
          <p className="sld-summary__label">
            <span className="sld-summary__icon sld-summary__icon--yellow"><FaTruck /></span>
            Est. Delivery
          </p>
          <p className="sld-summary__value">{estDelivery}</p>
        </div>
      </section>

      <div className="sld-toolbar">
        <div className="sld-toolbar__left">
          <label className="sld-toolbar__select">
            <input type="checkbox" checked={allVisibleSelected} disabled={saving} onChange={toggleSelectAll} />
            <span>Select All ({visibleItems.length})</span>
          </label>
          <div className="sld-toolbar__dropdown-wrap" ref={moveMenuRef}>
            <button
              type="button"
              className="sld-toolbar__dropdown-btn"
              disabled={saving || !otherLists.length}
              onClick={() => setMoveMenuOpen((open) => !open)}
            >
              Move to another list <FaChevronDown aria-hidden />
            </button>
            {moveMenuOpen ? (
              <div className="sld-toolbar__menu">
                {otherLists.length ? (
                  otherLists.map((list) => (
                    <button key={list.id} type="button" disabled={saving} onClick={() => handleMoveToList(list.id)}>
                      {list.name}
                    </button>
                  ))
                ) : (
                  <button type="button" disabled>No other lists</button>
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className="sld-toolbar__right">
          <label className="sld-toolbar__dropdown-btn sld-toolbar__sort">
            Sort by:
            <select value={sortBy} disabled={saving} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Recently Added</option>
              <option value="name">Name</option>
              <option value="price">Price</option>
            </select>
          </label>
          <button
            type="button"
            className="sld-toolbar__filter"
            disabled={saving}
            onClick={() => setStockFilter((current) => (current === "all" ? "in-stock" : "all"))}
          >
            <FaFilter aria-hidden />
            {stockFilter === "in-stock" ? "In Stock" : "Filter"}
          </button>
        </div>
      </div>

      <section className="sld-list" aria-label="Saved list items">
        {visibleSuppliers.map((supplier) => (
          <article key={supplier.id} className="sld-supplier">
            <header className="sld-supplier__head">
              <div className="sld-supplier__meta">
                <span className="sld-supplier__label">Supplier:</span>
                <strong>{supplier.name}</strong>
                {supplier.verified ? (
                  <span className="sld-supplier__verified">
                    <FaCheckCircle aria-hidden /> Verified Supplier
                  </span>
                ) : null}
                <span className="sld-supplier__muted">GSTIN: {supplier.gstin}</span>
                <span className="sld-supplier__muted">Lead Time: {supplier.items?.[0]?.leadTime || "—"}</span>
              </div>
              <span className="sld-supplier__delivery">Est. Delivery: {supplier.estDelivery}</span>
            </header>

            {(supplier.items || []).map((item) => {
              const lineTotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
              const moq = Math.max(1, Number(item.moq) || 1);
              return (
                <div key={item.id} className="sld-item">
                  <label className="sld-item__check">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      disabled={saving}
                      onChange={() => toggleItem(item.id)}
                    />
                  </label>

                  <Link
                    to={`/marketplace/products/${item.productId}`}
                    className="sld-item__thumb-link"
                    aria-label={`View ${item.name}`}
                  >
                    <ProductThumb name={item.name} imageUrl={item.imageUrl} />
                  </Link>

                  <div className="sld-item__body">
                    <h2 className="sld-item__title">
                      <Link to={`/marketplace/products/${item.productId}`}>{item.name}</Link>
                    </h2>
                    <p className="sld-item__sku">SKU: {item.sku}</p>
                    <StarRating />
                    <div className="sld-item__badges">
                      <span className={`sld-item__stock${item.inStock ? " sld-item__stock--in" : " sld-item__stock--out"}`}>
                        {item.inStock ? "In Stock" : "Out of Stock"}
                      </span>
                      <span>MOQ: {item.moq}</span>
                      <span>Lead Time: {item.leadTime}</span>
                      <span>Unit: {item.unit}</span>
                    </div>
                  </div>

                  <div className="sld-item__price-col">
                    <span className="sld-item__unit">
                      {formatCartInr(item.unitPrice)} / {item.unit}
                    </span>
                    <strong className="sld-item__total">{formatCartInr(lineTotal)}</strong>
                  </div>

                  <div className="sld-item__qty">
                    <label>Qty</label>
                    <div className="sld-item__qty-controls">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        disabled={saving || item.quantity <= moq}
                        onClick={() => handleQuantityChange(item, -1)}
                      >
                        <FaMinus aria-hidden />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        disabled={saving}
                        onClick={() => handleQuantityChange(item, 1)}
                      >
                        <FaPlus aria-hidden />
                      </button>
                    </div>
                  </div>

                  <div className="sld-item__actions">
                    <button
                      type="button"
                      className="sld-item__action sld-item__action--cart"
                      disabled={saving}
                      onClick={() => handleAddToCart([item.productId])}
                    >
                      <FaShoppingCart aria-hidden /> Add to Cart
                    </button>
                    <button
                      type="button"
                      className="sld-item__action sld-item__action--wishlist"
                      disabled={saving}
                      onClick={() => handleMoveToWishlist(item)}
                    >
                      <FaHeart aria-hidden /> Move to Wishlist
                    </button>
                    <button
                      type="button"
                      className="sld-item__action sld-item__action--remove"
                      disabled={saving}
                      onClick={() => handleRemove(item)}
                    >
                      <FaRegTrashAlt aria-hidden /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </article>
        ))}
      </section>

      <div className="sld-note">
        <FaInfoCircle aria-hidden />
        <span>Items remain in stock for 7 days. Prices may change based on market conditions.</span>
      </div>

      <div className="sld-footer">
        <div>
          <p className="sld-footer__total-label">Estimated Total</p>
          <p className="sld-footer__total-value">{formatCartInr(estimatedTotal)}</p>
          <p className="sld-footer__total-note">(Excluding taxes and shipping)</p>
        </div>
        <div className="sld-footer__actions">
          <button
            type="button"
            className="mysavedlists-btn mysavedlists-btn--primary"
            disabled={saving}
            onClick={() => handleAddToCart()}
          >
            <FaShoppingCart aria-hidden /> Add All to Cart ({selectedCount})
          </button>
          <button type="button" className="mysavedlists-btn mysavedlists-btn--ghost" disabled={saving} onClick={onRename}>
            Rename List
          </button>
          <button type="button" className="mysavedlists-btn mysavedlists-btn--danger" disabled={saving} onClick={onDelete}>
            Delete List
          </button>
        </div>
      </div>

      <MarketplaceRelatedProductsSection
        excludeProductIds={allItems.map((item) => item.productId)}
        saving={saving}
      />
    </>
  );
}
